import { MaybePromise, keys } from '@direct-frontend/stdlib';

import { BaseFields, InferFieldsValue } from '../fields/types';
import { cleanupValidationErrorsBySource } from '../fields/utils/cleanupValidationErrorsBySource';
import { getFormStoreSingleton } from '../store/store';
import { AllValidatorTypes, BaseValidationError, ValidationCause, ValidationState, ValidatorType } from '../types';

import { $internals as $formInternals, FormActionsMap, FormInstance } from './types';
import { $internals as $formApiInternals, FormApi, createFormApi } from './formApi';
import { assertFormInitialized } from './utils/assertFormInitialized';

/** Общий интерфейс валидатора формы */
export type FormValidator<
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContext,
> = (
    value: InferFieldsValue<FormFields>,
    formApi: FormApi<FormFields, FormValidationError, FormContext>,
    signal: AbortSignal
) => MaybePromise<void>;

// Возвращает список необходимых валидаторов в зависимости от причины запуска
function getValidatorsTypesByValidationCause(validationCause: ValidationCause): Array<'onChange' | 'onSubmit'> {
    switch (validationCause) {
        case 'change':
        case 'revalidate':
            return ['onChange'];
        case 'blur':
            return [];
        case 'submit':
            return ['onSubmit'];
    }
}

// Запускает необходимые валидаторы формы и возвращает объект с ошибками
export function runFormValidators<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validationCause: ValidationCause,
    validationState: ValidationState<FormValidationError>,
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >
) {
    const { updateValidationMeta, getFormValue, getFieldsState } = getFormStoreSingleton(form.name);
    const { formValidationErrorsMap, formValidationMeta } = validationState;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Тип гарантирован публичным интерфейсом
    const formValue = getFormValue({
        formInitializationId: form[$formInternals].formInitializationId,
    }) as InferFieldsValue<FormFields>;
    const fieldsState = getFieldsState({ formInitializationId: form[$formInternals].formInitializationId }) ?? {};

    // Сбрасываем предыдущие ошибки валидации в зависимости от причины запуска
    switch (validationCause) {
        case 'change':
        case 'revalidate':
            formValidationErrorsMap.onChange = [];
            formValidationMeta.onChange.controller?.abort();
            formValidationErrorsMap.onBlur = [];
            formValidationMeta.onBlur.controller?.abort();
            formValidationErrorsMap.onSubmit = [];
            formValidationMeta.onSubmit.controller?.abort();
            cleanupValidationErrorsBySource(fieldsState, validationState.fieldsValidation, { type: 'form' }, [
                'onChange',
                'onBlur',
                'onSubmit',
            ]);
            break;
        case 'blur':
            formValidationErrorsMap.onBlur = [];
            formValidationMeta.onBlur.controller?.abort();
            formValidationErrorsMap.onSubmit = [];
            formValidationMeta.onSubmit.controller?.abort();
            cleanupValidationErrorsBySource(fieldsState, validationState.fieldsValidation, { type: 'form' }, [
                'onBlur',
                'onSubmit',
            ]);
            break;
        case 'submit':
            formValidationErrorsMap.onSubmit = [];
            formValidationMeta.onSubmit.controller?.abort();
            cleanupValidationErrorsBySource(fieldsState, validationState.fieldsValidation, { type: 'form' }, [
                'onSubmit',
            ]);
            break;
    }

    const validatorsTypes = getValidatorsTypesByValidationCause(validationCause);

    for (const validatorsType of validatorsTypes) {
        // Перед запуском валидаторов создаем новый AbortController
        const controller = new AbortController();
        // И сбрасываем список промисов
        const promises: Promise<void>[] = [];

        const validatorsOfType = form[$formInternals].validators?.[validatorsType] ?? [];
        for (const validator of validatorsOfType) {
            const formApi: FormApi<FormFields, FormValidationError, FormContext> = createFormApi(
                validatorsType,
                { cause: validationCause, form },
                validationState
            );
            const validatorResult = validator(formValue, formApi, controller.signal);

            // Если валидатор асинхронный
            if (validatorResult instanceof Promise) {
                // То переключаем formApi в режим работы со слайсом, чтобы всегда работать с актуальным состоянием
                formApi[$formApiInternals].updateOptions({ storageType: 'slice' });
                // Так же запоминаем промис, чтобы родительские поля могли дождаться выполнения дочерних валидаторов
                promises.push(validatorResult);
            }
        }

        formValidationMeta[validatorsType] = { controller, promises };

        // Если среди валидаторов есть хотя бы один асинхронный, то
        if (promises.length > 0) {
            // после выполнения всех асинхронных валидаторов нужно очистить метаданные валидации формы
            Promise.allSettled(promises).then(() => {
                // Если запуск валидаторов был отменен, то ничего очищать не нужно
                // В таком случае список промисов в слайсе уже отличается от текущего
                if (controller.signal.aborted) {
                    return;
                }

                updateValidationMeta({
                    formInitializationId: form[$formInternals].formInitializationId,
                    formValidationMeta: {
                        [validatorsType]: {
                            controller: undefined,
                            promises: [],
                        },
                    },
                });
            });
        }
    }
}

// Запускает только собственные валидаторы формы (без валидации полей)
export function validateForm<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validationCause: ValidationCause,
    validationState: ValidationState<FormValidationError>,
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >
) {
    const { setValidationErrors } = getFormStoreSingleton(form.name);

    runFormValidators(validationCause, validationState, form);

    setValidationErrors({
        formInitializationId: form[$formInternals].formInitializationId,
        formValidationErrorsMap: validationState.formValidationErrorsMap,
        formValidationMeta: validationState.formValidationMeta,
        fieldsValidation: validationState.fieldsValidation,
    });
}

/**
 * Дожидается завершения всех асинхронных валидаторов определенного типа в форме и всех её дочерних полях
 * @param form Инстанс формы
 * @param validatorTypes Список типов валидаторов, которые необходимо дождаться
 * @returns Promise, который зарезолвится когда все необходимые валидаторы будут выполнены
 */
export function waitForAllRunningValidations<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    validatorTypes: ValidatorType[]
) {
    assertFormInitialized({
        formName: form.name,
        formId: form.id,
        formInitializationId: form[$formInternals].formInitializationId,
    });
    const { getFormValidationMeta, getFieldsState } = getFormStoreSingleton(form.name);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- ассерт выше гарантирует наличие значения
    const formValidationMeta = getFormValidationMeta({
        formInitializationId: form[$formInternals].formInitializationId,
    })!;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- ассерт выше гарантирует наличие значения
    const fieldsState = getFieldsState({ formInitializationId: form[$formInternals].formInitializationId })!;

    return Promise.allSettled([
        ...validatorTypes.flatMap(validatorType => formValidationMeta[validatorType].promises ?? []),
        ...validatorTypes.flatMap(validatorType =>
            keys(fieldsState).flatMap(fieldId => fieldsState[fieldId]?.validationMeta?.[validatorType].promises ?? [])
        ),
    ]);
}

/**
 * Отменяет все запущенные асинхронные валидаторы формы и всех её полей.
 * Результат отменённого валидатора в состояние не записывается.
 * @param form Инстанс формы
 */
export function abortAllRunningValidations<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >
) {
    const formInitializationId = form[$formInternals].formInitializationId;
    assertFormInitialized({ formName: form.name, formId: form.id, formInitializationId });

    const { getFormValidationMeta, getFieldsState } = getFormStoreSingleton(form.name);
    const formValidationMeta = getFormValidationMeta({ formInitializationId });
    const fieldsState = getFieldsState({ formInitializationId }) ?? {};

    for (const validatorType of AllValidatorTypes) {
        formValidationMeta?.[validatorType].controller?.abort();

        for (const fieldId of keys(fieldsState)) {
            fieldsState[fieldId]?.validationMeta?.[validatorType].controller?.abort();
        }
    }
}
